import type { RoadLabelTier } from './roadHierarchy';
import type { ExpressionSpecification, DataDrivenPropertyValueSpecification } from 'maplibre-gl';
import type { BASEMAP_LABEL_GROUPS, ResolvedBasemapLabelPresentation } from './labelPresentation';

export type BasemapLabelGroup = (typeof BASEMAP_LABEL_GROUPS)[number];

export const BASEMAP_LABEL_HALOS = ['off', 'subtle', 'standard', 'strong', 'maximum'] as const;
export const ROAD_LABEL_DENSITIES = ['low', 'normal', 'high'] as const;
export interface BasemapLabelAppearance {
  readonly size: number;
  readonly roadDensity?: (typeof ROAD_LABEL_DENSITIES)[number];
  readonly roadTiers?: Readonly<Record<RoadLabelTier, boolean>>;
  readonly emphasis: number;
  readonly halo: (typeof BASEMAP_LABEL_HALOS)[number];
}
export const DEFAULT_LABEL_APPEARANCE: BasemapLabelAppearance = {
  size: 1,
  emphasis: 0,
  halo: 'standard',
};
export function isBasemapLabelAppearance(value: unknown): value is BasemapLabelAppearance {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    Object.keys(record).every((key) =>
      ['size', 'emphasis', 'halo', 'roadDensity', 'roadTiers'].includes(key),
    ) &&
    (record.roadDensity === undefined ||
      ROAD_LABEL_DENSITIES.some((density) => density === record.roadDensity)) &&
    (record.roadTiers === undefined ||
      (typeof record.roadTiers === 'object' &&
        record.roadTiers !== null &&
        !Array.isArray(record.roadTiers) &&
        Object.keys(record.roadTiers).length === 4 &&
        ['motorwaysHighways', 'arterialRoads', 'collectorRoads', 'localRoads'].every(
          (key) => typeof (record.roadTiers as Record<string, unknown>)[key] === 'boolean',
        ))) &&
    typeof record.size === 'number' &&
    Number.isFinite(record.size) &&
    record.size >= 0.5 &&
    record.size <= 1.5 &&
    typeof record.emphasis === 'number' &&
    Number.isFinite(record.emphasis) &&
    record.emphasis >= -1 &&
    record.emphasis <= 2 &&
    BASEMAP_LABEL_HALOS.some((halo) => halo === record.halo)
  );
}
export function isDefaultLabelAppearance(value = DEFAULT_LABEL_APPEARANCE): boolean {
  return (
    value.size === 1 &&
    value.emphasis === 0 &&
    value.halo === 'standard' &&
    (value.roadDensity ?? 'normal') === 'normal' &&
    (!value.roadTiers || Object.values(value.roadTiers).every(Boolean))
  );
}
/** Keep camera expressions at the top level, as required by the MapLibre style contract. */
export function scaleLabelValue(
  value: DataDrivenPropertyValueSpecification<number>,
  factor: number,
): DataDrivenPropertyValueSpecification<number> {
  if (factor === 1) return value;
  if (typeof value === 'number') return value * factor;
  if (!Array.isArray(value)) {
    if (!('stops' in value))
      return ['*', ['to-number', ['get', value.property], value.default ?? 16], factor];
    return {
      ...value,
      stops: value.stops.map(([stop, output]) => [stop, output * factor]),
      ...('default' in value && value.default !== undefined
        ? { default: value.default * factor }
        : {}),
    } as DataDrivenPropertyValueSpecification<number>;
  }
  const result = [...value];
  if (value[0] === 'interpolate' && JSON.stringify(value[2]) === '["zoom"]') {
    for (let index = 4; index < result.length; index += 2)
      result[index] = scaleLabelValue(result[index] as number | ExpressionSpecification, factor);
    return result as ExpressionSpecification;
  }
  if (value[0] === 'step' && JSON.stringify(value[1]) === '["zoom"]') {
    result[2] = scaleLabelValue(result[2] as number | ExpressionSpecification, factor);
    for (let index = 4; index < result.length; index += 2)
      result[index] = scaleLabelValue(result[index] as number | ExpressionSpecification, factor);
    return result as ExpressionSpecification;
  }
  return ['*', value, factor];
}
/** Family-relative contrast: Strong brightens Dark and darkens Light without swapping role hues. */
export function labelEmphasisColour(colour: string, emphasis: number, dark: boolean): string {
  if (emphasis === 0) return colour;
  // Preserve the original [-1, 1] mapping exactly; only the added (1, 2] interval changes.
  const legacyTarget = dark ? '#f4f6f8' : '#1c2931';
  if (emphasis > 1) {
    return mixLabelColour(
      labelEmphasisColour(colour, 1, dark),
      dark ? '#ffffff' : '#080e12',
      (emphasis - 1) * 0.72,
    );
  }
  const target = emphasis < 0 ? (dark ? '#24282d' : '#fafbf8') : legacyTarget;
  const amount = Math.abs(emphasis) * (emphasis < 0 ? 0.32 : 0.45);
  return mixLabelColour(colour, target, amount);
}
function mixLabelColour(colour: string, target: string, amount: number): string {
  return (
    '#' +
    [1, 3, 5]
      .map((index) =>
        Math.round(
          parseInt(colour.slice(index, index + 2), 16) * (1 - amount) +
            parseInt(target.slice(index, index + 2), 16) * amount,
        )
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}
/** Designed baselines, independent of missing/zero provider paint values. */
export function labelHaloBaseline(
  group: BasemapLabelGroup,
  profile: ResolvedBasemapLabelPresentation,
): number {
  const baseline = {
    citiesTowns: 1.5,
    suburbsLocalities: 0.7,
    roadNames: 0.55,
    routeShields: 0.5,
    naturalWaterLabels: 0.8,
    otherLabels: 0.7,
    pointsOfInterest: 0.6,
    transitLabels: 0.6,
  }[group];
  const profileFactor =
    profile === 'detailed'
      ? 0.9
      : profile === 'minimal' && group === 'citiesTowns'
        ? 1.1
        : profile === 'thematic'
          ? group === 'citiesTowns'
            ? 1.1
            : 1.2
          : 1;
  return baseline * profileFactor;
}
export function labelHaloFactor(
  halo: BasemapLabelAppearance['halo'],
  group: BasemapLabelGroup,
  profile: ResolvedBasemapLabelPresentation,
): number {
  if (halo === 'off') return 0;
  if (halo === 'standard') return 1;
  if (halo === 'maximum') return labelHaloFactor('strong', group, profile) * 1.45;
  if (halo === 'subtle')
    return group === 'citiesTowns' ? 0.45 : group === 'naturalWaterLabels' ? 0.5 : 0.4;
  // Richer profiles keep added small text restrained even at the Strong endpoint.
  if (profile === 'detailed') return group === 'citiesTowns' ? 1.65 : 1.8;
  return group === 'citiesTowns' ? 1.6 : group === 'otherLabels' ? 1.85 : 2;
}
export function labelHaloColour(group: BasemapLabelGroup, dark: boolean): string {
  if (group === 'naturalWaterLabels')
    return dark ? 'rgba(21,32,40,0.96)' : 'rgba(250,252,253,0.96)';
  if (group === 'roadNames' || group === 'routeShields')
    return dark ? 'rgba(28,33,39,0.94)' : 'rgba(255,253,249,0.94)';
  return dark ? 'rgba(24,29,35,0.94)' : 'rgba(250,251,248,0.94)';
}

/** Bound the added maximum to the SDF glyph buffer without nesting zoom expressions. */
export function boundLabelHalo(
  width: DataDrivenPropertyValueSpecification<number>,
  size: DataDrivenPropertyValueSpecification<number>,
): DataDrivenPropertyValueSpecification<number> {
  if (typeof width === 'object' && !Array.isArray(width)) return width;
  if (typeof size === 'number')
    return typeof width === 'number'
      ? Math.min(width, size / 4)
      : ['min', width as ExpressionSpecification, size / 4];
  if (Array.isArray(size) && size[0] === 'interpolate' && JSON.stringify(size[2]) === '["zoom"]') {
    const output: unknown[] = [...size];
    for (let i = 4; i < output.length; i += 2) {
      const baseline = Array.isArray(width) && width[0] === 'interpolate' ? width[i] : width;
      output[i] = ['min', baseline, ['*', size[i], 0.25]];
    }
    return output as ExpressionSpecification;
  }
  return width;
}
